// ===================================================================
// DEBRIS ADMS — Module Layout with grouped sub-navigation
// Automated Debris Management System — organized nav sections
// ===================================================================

import { Outlet, useLocation } from 'react-router-dom';
import {
  IconLayoutDashboard, IconMap2, IconBuildingFactory2,
  IconUsers, IconTruck, IconTicket, IconBuildingWarehouse,
  IconFileInvoice, IconFileText, IconChartBar,
  IconSteeringWheel, IconMapRoute, IconReportAnalytics,
  IconUpload, IconTower, IconMapPin, IconPhoto, IconInbox,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import ModuleNav from '../../components/layout/ModuleNav';
import type { NavItem } from '../../types/nav';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
// brand color handled by ModuleNav

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',      path: '/debris',              icon: IconLayoutDashboard, exact: true },
  { label: 'Requests',       path: '/debris/requests',     icon: IconInbox },
  { label: 'Zones',          path: '/debris/zones',        icon: IconMap2 },
  { label: 'Contractors',    path: '/debris/contractors',  icon: IconBuildingFactory2 },
  { label: 'Crews',          path: '/debris/crews',        icon: IconUsers },
  { label: 'Drivers',        path: '/debris/drivers',      icon: IconSteeringWheel },
  { label: 'Trucks',         path: '/debris/trucks',       icon: IconTruck },
  { label: 'Load Tickets',   path: '/debris/tickets',      icon: IconTicket },
  { label: 'Field Pickup',  path: '/debris/pickup',       icon: IconMapPin },
  { label: 'Disposal Sites', path: '/debris/sites',        icon: IconBuildingWarehouse },
  { label: 'Monitors',       path: '/debris/monitors',    icon: IconTower },
  { label: 'Right-of-Entry', path: '/debris/roe',         icon: IconTicket },
  { label: 'Photos',        path: '/debris/photos',       icon: IconPhoto },
  { label: 'Map',            path: '/debris/map',          icon: IconMap2 },
  { label: 'Routes',         path: '/debris/routes',       icon: IconMapRoute, minLevel: 'editor' },
  { label: 'Monitor',        path: '/debris/monitor',      icon: IconTower },
  { label: 'Import',         path: '/debris/import',       icon: IconUpload, minLevel: 'editor' },
  { label: 'Rate Schedule',   path: '/debris/rates',        icon: IconFileInvoice, minLevel: 'editor' },
  { label: 'Invoices',       path: '/debris/invoices',     icon: IconFileInvoice, minLevel: 'editor' },
  { label: 'Reports',        path: '/debris/reports',      icon: IconReportAnalytics, minLevel: 'editor' },
  { label: 'Driver Resources', path: '/debris/documents',  icon: IconFileText },
  { label: 'Analytics',      path: '/debris/analytics',    icon: IconChartBar, minLevel: 'editor' },
];

// Group nav items by section for organized display
const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/debris', '/debris/requests'] },
  { label: 'Setup', paths: ['/debris/zones', '/debris/contractors', '/debris/crews', '/debris/drivers', '/debris/trucks', '/debris/sites', '/debris/monitors', '/debris/roe'] },
  { label: 'Operations', paths: ['/debris/tickets', '/debris/pickup', '/debris/monitor', '/debris/routes'] },
  { label: 'Data', paths: ['/debris/photos', '/debris/map', '/debris/import'] },
  { label: 'Admin', paths: ['/debris/rates', '/debris/invoices', '/debris/reports', '/debris/documents', '/debris/analytics'] },
];

export default function DebrisLayout() {
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('debris', NAV_ITEMS);

  // Build visible items per group
  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => visibleItems.find(vi => vi.path === p))
      .filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumbs
        items={[
          { label: 'Hub', to: '/' },
          { label: 'Debris', to: '/debris' },
          ...(currentPage && currentPage.path !== '/debris' ? [{ label: currentPage.label }] : []),
        ]}
      />
      <ModuleNav items={visibleItems} groups={groups} />
      <Outlet />
    </div>
  );
}
